/** Sidebar width preference: THREE states, not two. 'expanded'/'collapsed' are choices made with the pin button; `null` means not chosen, which lets the dashboard open the rail by default without overriding somebody who closed it.
 *  Precedence is pin > page > collapsed. Hover is handled in CSS and never writes anything down. */
export type RailPin = 'expanded' | 'collapsed'

const STORAGE_KEY = 'trg-rail-pin'

export function getStoredRailPin(): RailPin | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    if (value === 'expanded' || value === 'collapsed') return value
  } catch {
    // localStorage can throw in locked-down iframes; fall back to the default.
  }
  return null
}

export function storeRailPin(pin: RailPin): void {
  try {
    localStorage.setItem(STORAGE_KEY, pin)
  } catch {
    // Non-fatal: the choice just won't persist.
  }
}

/** Whether the rail is docked open. Icon mode is the default except on the first page of a session, where the rail opens once so a new arrival can see the product; after any navigation it stays collapsed to give width to the grid and tables.
 *  An explicit pin beats the default in either direction.
 *  A phone overrules both: the docked rail reserves 240px of a 358px usable width, so no reading of "expanded" applies there, even a pin set at a desk on the same profile. See `PHONE_QUERY`. */
export function isRailDocked(
  pin: RailPin | null,
  firstPageOfSession: boolean,
  isPhone = false,
): boolean {
  if (isPhone) return false
  if (pin) return pin === 'expanded'
  return firstPageOfSession
}

/** What counts as a phone for the rail: 640px is Tailwind's `sm:`, the same line `rosterPhoneRedirect` acts on, so a phone reader never gets the desk-width rail. */
export const PHONE_QUERY = '(max-width: 639.98px)'

/** Whether the user has navigated yet this session. `sessionStorage`, not React state: scoped to the tab, and it survives a reload so a refresh does not pop the rail back open. */
const NAVIGATED_KEY = 'trg-rail-navigated'

export function hasNavigated(): boolean {
  try {
    return sessionStorage.getItem(NAVIGATED_KEY) === '1'
  } catch {
    // Locked-down iframe: treat as a fresh landing rather than throwing.
    return false
  }
}

export function markNavigated(): void {
  try {
    sessionStorage.setItem(NAVIGATED_KEY, '1')
  } catch {
    // Non-fatal: the rail just opens once more than it should.
  }
}
